import { Box, Button, Checkbox, Dialog, IconButton } from "@mui/material";
import { ChangeEvent, Dispatch, DragEvent, SetStateAction, useId, useState } from "react";
import { FileUpload, UploaderArgs, UploaderConfig } from "./multipart-upload.types";
import FileUploadProgress from "./FileUploadProgress";
import { Close, Upload } from "@mui/icons-material";
import produce from "immer";
import { Uploader } from "./Uploader";
import { P } from "theme/typography";
import theme from "theme";
import { StageNumber } from "shared/StageNumber";
import { useAppSelector } from "redux/hooks";
import { selectFormSectionsMap } from "redux/slices/form.slice";
import { selectSectionValues } from "redux/slices/case";
import { SectionReviewState, SectionValueView, ValidationStatus } from "generated/graphql";
import { DisplaySectionOutcome, DisplayValidationIssue } from "SBR/DisplaySectionOutcome";
import { REVIEW_STATE_UI } from "SBR/REVIEW_STATE_UI";
import { cn } from "components/lib/utils";
import { useSectionValues } from "SBR/useSectionValues";
import { AssigneeChip } from "SBR/AssigneeChip";
import { SectionValueAssignment } from "SBR/SectionValueAssignment";
import { pluralize } from "helpers/coll";

function SectionDisplay({
  sectionValues,
  reviewState,
  selectedSectionIds,
  removeSectionId,
  addSectionId,
  disableSelection,
}: {
  sectionValues: SectionValueView[];
  reviewState: SectionReviewState;
  selectedSectionIds: number[];
  removeSectionId: (id: number) => void;
  addSectionId: (id: number) => void;
  disableSelection: boolean;
}) {
  const formSelectionsMap = useAppSelector(selectFormSectionsMap);

  return (
    <div
      className={cn("border border-gray-100 p-4 rounded-lg flex flex-col gap-2", REVIEW_STATE_UI[reviewState].color)}>
      <p className="font-semibold uppercase">{REVIEW_STATE_UI[reviewState].name}</p>
      {sectionValues.map((sv) => {
        return (
          <div className="flex gap-4 items-center" key={sv.sectionId}>
            <Checkbox
              disabled={disableSelection}
              size="small"
              className="pointer-events-auto"
              checked={selectedSectionIds.includes(sv.sectionId)}
              onChange={(evt) => (evt.target.checked ? addSectionId(sv.sectionId) : removeSectionId(sv.sectionId))}
            />
            <p className="text-xs w-96">{formSelectionsMap.get(sv.sectionId)?.name ?? "Section"}</p>
            <div className="flex gap-2 items-center w-60">
              <>
                <DisplaySectionOutcome outcomeStatus={sv.outcomeStatus} className="text-xs" />
                {sv.validationStatus === ValidationStatus.Issue && (
                  <>
                    <p className="text-gray-100">•</p>
                    <DisplayValidationIssue validationStatus={sv.validationStatus} className="text-xs" />
                  </>
                )}
              </>
            </div>
            <SectionValueAssignment sectionValue={sv}>
              {({ userIsOverallAssignee, assignee, showQAInfo }) => (
                <AssigneeChip
                  className="cursor-default"
                  user={assignee}
                  isMe={userIsOverallAssignee}
                  tooltipRole={{ isQa: showQAInfo }}
                />
              )}
            </SectionValueAssignment>
          </div>
        );
      })}
    </div>
  );
}

function SectionsDisplay({
  selectedSectionIds,
  removeSectionId,
  addSectionId,
  sectionsInReview,
  sectionsInValidation,
  sectionsInComplete,
  uploadInProgress,
}: {
  selectedSectionIds: number[];
  removeSectionId: (id: number) => void;
  addSectionId: (id: number) => void;
  sectionsInReview: SectionValueView[];
  sectionsInValidation: SectionValueView[];
  sectionsInComplete: SectionValueView[];
  uploadInProgress: boolean;
}) {
  return (
    <>
      {sectionsInReview.length > 0 && (
        <SectionDisplay
          disableSelection
          sectionValues={sectionsInReview}
          reviewState={SectionReviewState.Review}
          selectedSectionIds={selectedSectionIds}
          removeSectionId={removeSectionId}
          addSectionId={addSectionId}
        />
      )}
      {sectionsInValidation.length > 0 && (
        <SectionDisplay
          disableSelection={uploadInProgress}
          sectionValues={sectionsInValidation}
          reviewState={SectionReviewState.Validation}
          selectedSectionIds={selectedSectionIds}
          removeSectionId={removeSectionId}
          addSectionId={addSectionId}
        />
      )}
      {sectionsInComplete.length > 0 && (
        <SectionDisplay
          disableSelection={uploadInProgress}
          sectionValues={sectionsInComplete}
          reviewState={SectionReviewState.Complete}
          selectedSectionIds={selectedSectionIds}
          removeSectionId={removeSectionId}
          addSectionId={addSectionId}
        />
      )}
    </>
  );
}

export function SBRUploadDialog({
  open,
  close,
  caseId,
  claimantName,
  UploaderClazz,
  onUnAuthorized,
  setUploads,
}: {
  open: boolean;
  close: () => void;
  caseId: number;
  claimantName: string;
  UploaderClazz: new (args: UploaderArgs) => Uploader;
  onUnAuthorized: () => void;
  setUploads: Dispatch<SetStateAction<Uploader[]>>;
}) {
  const [isDraggedOn, setIsDraggedOn] = useState<boolean>(false);
  const batchId = useId();
  const [files, setFiles] = useState<FileUpload[]>([]);
  const anyFilesUploading = files.some((f) => f.state === "inprogress" || f.state === "queued");
  const allFilesUploaded =
    Boolean(files.length) && files.every((f) => f.state !== "idle" && (f.state === "error" || f.state === "success"));
  const successFiles = files.filter((f) => f.state === "success");
  const errorFiles = files.filter((f) => f.state === "error" && !f.clientError);

  const batchComplete = allFilesUploaded;
  const batchHadErrors = allFilesUploaded && errorFiles.length > 0;

  const invalidClientErrorFiles = files.filter((f) => f.clientError);
  const isClientErrors = invalidClientErrorFiles.length > 0;
  const anyFilesUploadingOrHaveUploaded: boolean =
    anyFilesUploading || Boolean(successFiles.length) || Boolean(errorFiles.length);
  const sectionValues = useAppSelector(selectSectionValues);
  const sectionValuesHook = useSectionValues(sectionValues);
  const [selectedSectionIds, setSelectionIds] = useState<number[]>(
    sectionValuesHook.reviewSections.map((s) => s.sectionId)
  );
  const sectionIdsGoingToReview = selectedSectionIds.filter(
    (id) =>
      sectionValuesHook.qaSections.map((s) => s.sectionId).includes(id) ||
      sectionValuesHook.completedSections.map((s) => s.sectionId).includes(id)
  );
  const preserveSectionStateForSections = sectionValuesHook.qaSections
    .filter((s) => !selectedSectionIds.includes(s.sectionId))
    .concat(sectionValuesHook.completedSections.filter((s) => !selectedSectionIds.includes(s.sectionId)))
    .map((sv) => sv.sectionId);

  function addSectionId(id: number) {
    setSelectionIds((ids) => [...ids, id]);
  }
  function removeSectionId(id: number) {
    setSelectionIds((ids) => ids.filter((i) => i !== id));
  }
  function selectAllSectionIds() {
    setSelectionIds(sectionValuesHook.allSectionIds);
  }
  function deselectAllSectionIds() {
    setSelectionIds(sectionValuesHook.reviewSections.map((s) => s.sectionId));
  }

  const handleFiles = (fileList: File[]) => {
    const newUploads: FileUpload[] = fileList
      .filter((f) => !files.find((file) => file.file.name === f.name))
      .map((f) => {
        const baseFile = {
          file: f,
          batchId,
          progress: 0,
          bytesSent: 0,
        };
        if (f.size > UploaderConfig.maxUploadSizeBytes) {
          return {
            ...baseFile,
            state: "error",
            clientError: "size_exceeded",
          };
        } else if (!UploaderConfig.checkUploadType(f.type)) {
          return {
            ...baseFile,
            state: "error",
            clientError: "invalid_type",
          };
        } else if (f.size === 0) {
          return {
            ...baseFile,
            state: "error",
            clientError: "empty_document",
          };
        } else {
          return {
            ...baseFile,
            state: "idle",
          };
        }
      });
    setFiles((prev) => [...prev, ...newUploads]);
  };

  const onFiles = (evt: ChangeEvent<HTMLInputElement>) => {
    handleFiles(Array.from(evt.target.files || []));
  };

  const beginUploads = () => {
    const validFiles = files.filter((f) => f.state === "idle" && !f.error && !f.clientError);
    const uploaders = validFiles.map((file, idx) => {
      setFiles((prev) =>
        produce(prev, (draft) => {
          const val = draft[idx];

          if (val !== undefined) {
            val.state = "queued";
          }
        })
      );
      const uploader = new UploaderClazz({
        file: file.file,
        caseId,
        batchId,
        preserveSectionStateForSections,
        onComplete: () => {
          setFiles((prev) =>
            produce(prev, (draft) => {
              const val = draft[idx];

              if (val !== undefined) {
                val.state = "success";
              }
            })
          );
        },
        onProgress: (_partIndex, _partProgress, _partTotal, totalSent) => {
          setFiles((prev) =>
            produce(prev, (draft) => {
              const val = draft[idx];

              if (val !== undefined) {
                val.progress = (totalSent / file.file.size) * 100;
                if (val.state === "idle" || val.state === "queued") {
                  val.state = "inprogress";
                }
              }
            })
          );
        },
        onFailure: (reason: Uploader["failureReason"], message?: string) => {
          if (reason === "unauthorized") {
            onUnAuthorized();
          }
          setFiles((prev) =>
            produce(prev, (draft) => {
              const val = draft[idx];

              if (val !== undefined) {
                val.error = (reason !== "unknown" && reason) || message || "Error";
                val.state = "error";
              }
            })
          );
        },
      });
      uploader.sendBeginUpload();
      return uploader;
    });
    setUploads((prev) => [...prev, ...uploaders]);
  };

  const onFileDelete = (file: FileUpload) => {
    setFiles((currFiles) => currFiles.filter((cf) => cf !== file));
  };

  return (
    <Dialog open={open} onClose={anyFilesUploading ? undefined : close} fullWidth maxWidth="xl">
      <div
        className="p-4 mb-4 pointer-events-auto"
        onDrop={(evt: DragEvent<HTMLDivElement>) => {
          evt.preventDefault();
          evt.stopPropagation();

          if (evt.dataTransfer.files.length > 0) {
            handleFiles([...evt.dataTransfer.files]);
          }
          setIsDraggedOn(false);
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          event.stopPropagation();

          setIsDraggedOn(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          event.stopPropagation();

          setIsDraggedOn(false);
        }}
        onDragEnd={(event) => {
          event.preventDefault();
          event.stopPropagation();

          setIsDraggedOn(false);
        }}
        onDragOver={(e) => e.preventDefault()}>
        <div className="flex items-center justify-between pointer-events-none">
          <P sx={{ color: "grey.400", mr: 1 }} variant="subtitle1">
            Uploading files to: <strong>{claimantName}</strong>
          </P>
          {!anyFilesUploading && (
            <IconButton className="pointer-events-auto" onClick={close}>
              <Close />
            </IconButton>
          )}
        </div>

        <div className="grid grid-cols-[1fr_3fr] gap-4 pointer-events-none">
          <div
            className={cn(
              "flex flex-col border border-dashed border-gray-100 p-4 items-start rounded-lg",
              anyFilesUploadingOrHaveUploaded ? "opacity-50" : "",
              isDraggedOn ? "bg-peri-100" : ""
            )}>
            <div className="flex items-center gap-2">
              <StageNumber step={1} className="mb-0" />
              <p className="font-semibold">Select files</p>
            </div>
            <div className="flex flex-col h-full w-full items-center justify-center">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                  pointerEvents: "none",
                }}>
                <Box
                  sx={{
                    backgroundColor: "rgb(147 197 253)",
                    height: 40,
                    width: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                  }}>
                  <Upload />
                </Box>
                <P variant="subtitle1heavy">
                  <label
                    style={{
                      marginTop: "8px",
                      fontWeight: "bold",
                      textDecoration: "underline",
                      cursor: "pointer",
                      pointerEvents: "auto",
                      fontFamily: "Inter,arial,sans-serif",
                    }}>
                    Click to select files
                    <input
                      multiple={true}
                      type="file"
                      autoComplete="off"
                      tabIndex={-1}
                      accept="image/*,.pdf,.docx,.txt"
                      style={{ display: "none" }}
                      onChange={onFiles}
                      disabled={!!anyFilesUploadingOrHaveUploaded}
                    />
                  </label>
                </P>
                <P>or drag and drop files here</P>
                <P sx={{ mt: 1 }}>Supported File Types:</P>
                <P variant="subtitle2">
                  <strong>PDF, PNG, JPG, TIFF, TXT, DOCX</strong>
                </P>
              </Box>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div
              className={cn(
                "flex flex-col border border-gray-100 p-4 rounded-lg gap-4 grow",
                anyFilesUploadingOrHaveUploaded ? "opacity-50" : ""
              )}>
              {files.length > 0 && (
                <>
                  <div className="flex items-center gap-2">
                    <StageNumber
                      step={2}
                      color={batchComplete ? (batchHadErrors ? "red" : "green") : "grey"}
                      className="mb-0"
                      // className="pointer-events-none"
                    />
                    <p className="font-semibold">Select sections</p>
                  </div>
                  <div className="flex gap-4 items-center">
                    <p className="text-xs">
                      Once you&apos;re done adding files, select which sections to move to review.
                    </p>
                    <div className="grow" />
                    <Button size="small" variant="text" onClick={selectAllSectionIds} className="pointer-events-auto">
                      Select All
                    </Button>
                    <Button size="small" variant="text" onClick={deselectAllSectionIds} className="pointer-events-auto">
                      Deselect All
                    </Button>
                  </div>
                  <div className="flex flex-col gap-4">
                    <SectionsDisplay
                      uploadInProgress={anyFilesUploading}
                      sectionsInComplete={sectionValuesHook.completedSections}
                      sectionsInValidation={sectionValuesHook.qaSections}
                      sectionsInReview={sectionValuesHook.reviewSections}
                      selectedSectionIds={selectedSectionIds}
                      addSectionId={addSectionId}
                      removeSectionId={removeSectionId}
                    />
                  </div>
                </>
              )}
            </div>
            {sectionIdsGoingToReview.length > 0 && (
              <p className={cn("text-sm", anyFilesUploadingOrHaveUploaded ? "text-gray-100" : "")}>
                Note: {sectionIdsGoingToReview.length} {pluralize(sectionIdsGoingToReview.length, "section")} will be
                moved to review.
              </p>
            )}
          </div>
        </div>
        {files.length === 0 ? (
          <Box
            sx={{
              my: 2,
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              p: 2,
              border: `1px solid ${theme.palette.grey[100]}`,
            }}>
            <P sx={{ color: "grey.400" }}>Files will appear here.</P>
          </Box>
        ) : (
          <FileUploadProgress files={files} onFileDelete={onFileDelete} />
        )}

        <div className="flex justify-center pointer-events-none">
          {!batchComplete || files.length === 0 || isClientErrors ? (
            <Button
              variant="contained"
              className="pointer-events-auto"
              disabled={!files.length || anyFilesUploading || isClientErrors}
              onClick={beginUploads}>
              Begin Upload
            </Button>
          ) : (
            <Button variant="contained" onClick={close} className="pointer-events-auto">
              Close
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
